import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FiChevronDown } from 'react-icons/fi';
import { GET_VOTING_ARTICLES, GET_REPORTED_ARTICLES, GET_VOTING_STRATEGY } from '../graphql/voting';
import { useTags } from '../contexts/TagContext';
import { useAuth } from '../contexts/AuthContext';
import VotingCard from './VotingCard';
import VotingCardMobile from './VotingCardMobile';
import VotingDetailModal from './VotingDetailModal';
import CustomSelect from './CustomSelect';

const VotingList = ({ navigate, viewMode = 'all', onViewModeChange }) => {
  const { tags: availableTags, refreshTags } = useTags();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortBy, setSortBy] = useState('LATEST'); // LATEST, MOST_VOTED
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSearchTerm, setActiveSearchTerm] = useState(''); // 實際用於查詢的搜索詞
  const [selectedTagId, setSelectedTagId] = useState(''); // 選擇的標籤 ID
  const [activeTagId, setActiveTagId] = useState(''); // 實際用於查詢的標籤 ID
  const [onlyRecommended, setOnlyRecommended] = useState(false); // 推薦篩選
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedVotingId, setSelectedVotingId] = useState(null);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);
  const sortDropdownRef = useRef(null);
  const itemsPerPage = 15;
  
  // 被檢舉模式的篩選和排序狀態
  const [falseAlarmFilter, setFalseAlarmFilter] = useState('NONE');
  const [isReportedFilter, setIsReportedFilter] = useState(null);
  const [isRecommendedFilter, setIsRecommendedFilter] = useState(null);
  const [reportedSortBy, setReportedSortBy] = useState('REPORT_COUNT_DESC');

  // 解析 URL 參數 - URL 是唯一的真實來源
  const paramSort = searchParams.get('sort') === 'MOST_VOTED' ? 'MOST_VOTED' : 'LATEST';
  const paramSearch = searchParams.get('q') || '';
  const paramTag = searchParams.get('tag') || '';
  const paramRecommended = searchParams.get('recommended') === 'true';
  const paramPageRaw = parseInt(searchParams.get('page') || '1', 10);
  const paramPage = Number.isFinite(paramPageRaw) && paramPageRaw > 0 ? paramPageRaw : 1;
  
  // 被檢舉模式的篩選和排序參數
  const paramFalseAlarmFilter = searchParams.get('falseAlarmFilter') || 'NONE';
  const paramIsReported = searchParams.get('isReported') === 'true' ? true : searchParams.get('isReported') === 'false' ? false : null;
  const paramIsRecommended = searchParams.get('isRecommended') === 'true' ? true : searchParams.get('isRecommended') === 'false' ? false : null;
  const paramReportedSortBy = searchParams.get('reportedSortBy') || 'REPORT_COUNT_DESC';

  // 使用一個 ref 來追蹤上一次的 viewMode，避免初始化時觸發重置
  const prevViewModeRef = React.useRef(viewMode);

  // 當 viewMode 改變時重置頁碼（但只在真正改變時，不在初始化時）
  useEffect(() => {
    if (prevViewModeRef.current !== viewMode) {
      prevViewModeRef.current = viewMode;
      // 只有當當前頁碼不是 1 時才重置
      if (currentPage !== 1) {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('page', '1');
          return next;
        });
      }
    }
  }, [viewMode, currentPage, setSearchParams]);

  // 從 URL 同步所有狀態（URL 是唯一真實來源）
  useEffect(() => {
    setSortBy(paramSort);
    setSearchTerm(paramSearch);
    setActiveSearchTerm(paramSearch);
    setSelectedTagId(paramTag);
    setActiveTagId(paramTag);
    setOnlyRecommended(paramRecommended);
    setCurrentPage(paramPage);
    // 被檢舉模式的參數
    setFalseAlarmFilter(paramFalseAlarmFilter);
    setIsReportedFilter(paramIsReported);
    setIsRecommendedFilter(paramIsRecommended);
    setReportedSortBy(paramReportedSortBy);
  }, [paramSort, paramSearch, paramTag, paramRecommended, paramPage, paramFalseAlarmFilter, paramIsReported, paramIsRecommended, paramReportedSortBy]);

  // 如果進入投票廣場時發現 tags 是空的，要去查詢
  useEffect(() => {
    if (availableTags.length === 0) {
      console.log('VotingList: Tags are empty, refreshing tags...');
      refreshTags();
    }
  }, [availableTags.length, refreshTags]);

  // 點擊外部關閉排序下拉選單
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setIsSortDropdownOpen(false);
      }
    };

    if (isSortDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isSortDropdownOpen]);

  // 使用統一的查詢，根據是否有搜尋詞、標籤或使用者ID來決定參數
  const hasActiveSearchTerm = activeSearchTerm.trim().length > 0;
  const hasActiveTag = activeTagId.trim().length > 0;
  const isMyPostsMode = viewMode === 'myPosts' && user;
  const isReportedMode = viewMode === 'reported' && user?.isSemiAdmin;
  const isStrategyMode = viewMode === 'strategy' && user;
  
  // 被檢舉文章查詢
  const { loading: reportedLoading, error: reportedError, data: reportedData, refetch: refetchReported } = useQuery(
    GET_REPORTED_ARTICLES,
    {
      variables: {
        limit: itemsPerPage,
        skip: (currentPage - 1) * itemsPerPage,
        falseAlarmFilter: falseAlarmFilter !== 'NONE' ? falseAlarmFilter : undefined,
        isReported: isReportedFilter,
        isRecommended: isRecommendedFilter,
        sortBy: reportedSortBy
      },
      fetchPolicy: 'cache-and-network',
      skip: !isReportedMode // 只在被檢舉模式下查詢
    }
  );
  
  // 投票攻略查詢
  const { loading: strategyLoading, error: strategyError, data: strategyData, refetch: refetchStrategy } = useQuery(
    GET_VOTING_STRATEGY,
    {
      fetchPolicy: 'cache-and-network',
      skip: !isStrategyMode // 只在投票攻略模式下查詢
    }
  );

  // 一般文章查詢
  const { loading, error, data, refetch } = useQuery(
    GET_VOTING_ARTICLES,
    {
      variables: {
        limit: itemsPerPage,
        skip: (currentPage - 1) * itemsPerPage,
        sortBy: sortBy,
        searchTerm: hasActiveSearchTerm ? activeSearchTerm.trim() : undefined,
        tagId: hasActiveTag ? activeTagId : undefined,
        userId: isMyPostsMode ? user.id : undefined,
        onlyRecommended: onlyRecommended || undefined
      },
      fetchPolicy: 'cache-and-network',
      skip: (isMyPostsMode && !user) || isReportedMode || isStrategyMode // 如果選擇"我的投票文"但未登入，或是在被檢舉模式，或是投票攻略模式，跳過查詢
    }
  );
  
  // 根據模式選擇數據
  const currentLoading = isReportedMode ? reportedLoading : (isStrategyMode ? strategyLoading : loading);
  const currentError = isReportedMode ? reportedError : (isStrategyMode ? strategyError : error);
  const currentData = isReportedMode ? reportedData : (isStrategyMode ? strategyData : data);
  const currentRefetch = isReportedMode ? refetchReported : (isStrategyMode ? refetchStrategy : refetch);

  const handleVoteSuccess = (vote) => {
    console.log('投票成功:', vote);
    // 可以觸發通知或其他操作
  };

  // URL 同步：讀取初始 votingId
  useEffect(() => {
    const urlVotingId = searchParams.get('votingId');
    if (urlVotingId && urlVotingId !== selectedVotingId) {
      setSelectedVotingId(urlVotingId);
    }
    if (!urlVotingId && selectedVotingId !== null) {
      // 若 URL 移除參數則關閉
      setSelectedVotingId(null);
    }
  }, [searchParams, selectedVotingId]);

  const handleViewDetail = (votingId) => {
    setSelectedVotingId(votingId);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('votingId', votingId);
      return next;
    });
  };

  const handleCloseDetail = () => {
    setSelectedVotingId(null);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('votingId');
      return next;
    });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // 允許空白關鍵字（此時只用標籤篩選）
    const trimmed = searchTerm.trim();
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (trimmed) {
        next.set('q', trimmed);
      } else {
        next.delete('q');
      }
      next.set('page', '1');
      return next;
    });
  };

  // 清空搜索
  const handleClearSearch = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('q');
      next.delete('tag');
      next.set('page', '1');
      return next;
    });
  };

  // 處理標籤選擇
  const handleTagChange = (e) => {
    const tagId = e.target.value;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tagId) {
        next.set('tag', tagId);
      } else {
        next.delete('tag');
      }
      next.set('page', '1');
      return next;
    });
  };

  // 處理排序變化
  const handleSortChange = (newSortBy) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (newSortBy === 'LATEST') {
        next.delete('sort');
      } else {
        next.set('sort', newSortBy);
      }
      next.set('page', '1');
      return next;
    });
    setIsSortDropdownOpen(false);
  };

  const sortOptions = [
    { value: 'LATEST', label: '最新' },
    { value: 'MOST_VOTED', label: '最多投票' }
  ];

  const selectedSortOption = sortOptions.find(opt => opt.value === sortBy) || sortOptions[0];

  // 處理推薦篩選切換
  const handleToggleRecommended = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (onlyRecommended) {
        next.delete('recommended');
      } else {
        next.set('recommended', 'true');
      }
      next.set('page', '1');
      return next;
    });
  };

  // 計算總頁數
  const totalCount = isReportedMode 
    ? (currentData?.reportedArticles?.totalCount || 0)
    : (isStrategyMode 
      ? (currentData?.votingStrategy?.totalCount || 0)
      : (currentData?.votingArticles?.totalCount || 0));
  const totalPages = isStrategyMode ? 1 : Math.ceil(totalCount / itemsPerPage); // 投票攻略單頁顯示

  // 處理頁面變化
  const handlePageChange = (page) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(page));
      return next;
    });
    window.scrollTo({ top: 0, behavior: 'smooth' }); // 滾動到頂部
  };

  // 直接使用統一的後端返回數據
  const articles = isReportedMode
    ? (currentData?.reportedArticles?.articles?.map(item => ({ ...item.article, reportCount: item.reportCount })) || [])
    : (isStrategyMode
      ? (currentData?.votingStrategy?.articles || [])
      : (currentData?.votingArticles?.articles || []));

  if (currentLoading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (currentError) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 mb-4">載入投票失敗: {currentError.message}</div>
      </div>
    );
  }
  
  // 如果是在被檢舉模式但使用者不是半管理員
  if (viewMode === 'reported' && (!user || !user.isSemiAdmin)) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 mb-4">權限不足</div>
      </div>
    );
  }

  return (
    <div>
      {/* 搜索和篩選區域 */}
      {!isReportedMode && !isStrategyMode && (
      <div className="mb-6 space-y-4">
        {/* 標籤搜尋 */}
        <div>
          <CustomSelect
            label="選擇標籤"
            value={selectedTagId}
            onChange={handleTagChange}
            placeholder="所有標籤"
            options={[
              { value: '', label: '所有標籤' },
              ...availableTags.map(tag => ({
                value: tag.id,
                label: `${tag.name}${tag.description ? ` - ${tag.description}` : ''}`
              }))
            ]}
          />
        </div>

        {/* 關鍵字搜尋 + 排序按鈕 */}
        <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-2 items-stretch md:items-center">
          
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="搜索投票標題或內容..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-400">🔍</span>
            </div>
          </div>
          
          <div className="flex space-x-2">
            {/* 排序下拉選單 */}
            <div className="relative" ref={sortDropdownRef}>
              <button
                type="button"
                onClick={() => setIsSortDropdownOpen(!isSortDropdownOpen)}
                className="px-4 py-2 rounded-lg transition-all whitespace-nowrap flex items-center gap-1 bg-purple-400 text-white hover:bg-purple-500"
              >
                <span>{selectedSortOption.label}</span>
                <FiChevronDown
                  className={`w-4 h-4 transition-transform ${
                    isSortDropdownOpen ? 'transform rotate-180' : ''
                  }`}
                />
              </button>

              {isSortDropdownOpen && (
                <div className="absolute left-0 top-full mt-1 w-40 bg-white border-2 border-purple-200 rounded-lg shadow-xl z-50 overflow-hidden">
                  {sortOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => handleSortChange(option.value)}
                      className={`w-full px-4 py-3 text-left transition-colors text-sm ${
                        sortBy === option.value
                          ? 'bg-purple-50 text-purple-900 font-medium'
                          : 'text-gray-900 hover:bg-purple-50'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={handleToggleRecommended}
              className={`px-4 py-2 rounded-lg transition-colors whitespace-nowrap flex items-center gap-1 ${
                onlyRecommended
                  ? 'bg-gradient-to-r from-yellow-400 to-orange-400 text-white hover:from-yellow-500 hover:to-orange-500' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <span>⭐</span>
              <span>推薦</span>
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-purple-400 text-white rounded-lg hover:bg-purple-500 transition-colors whitespace-nowrap"
            >
              搜尋
            </button>
          </div>
        </form>

        {/* 搜尋結果提示 */}
        {!isReportedMode && (hasActiveSearchTerm || hasActiveTag || viewMode === 'myPosts' || onlyRecommended) && (
          <div className="text-sm text-gray-600">
            {viewMode === 'myPosts' && hasActiveSearchTerm && hasActiveTag && `我的投票文 - 搜索 "${activeSearchTerm}" 並篩選標籤的結果`}
            {viewMode === 'myPosts' && hasActiveSearchTerm && !hasActiveTag && `我的投票文 - 搜索 "${activeSearchTerm}" 的結果`}
            {viewMode === 'myPosts' && !hasActiveSearchTerm && hasActiveTag && `我的投票文 - 標籤 "${availableTags.find(t => t.id === activeTagId)?.name}" 的文章`}
            {viewMode === 'myPosts' && !hasActiveSearchTerm && !hasActiveTag && `我的投票文`}
            {viewMode === 'all' && hasActiveSearchTerm && hasActiveTag && `搜索 "${activeSearchTerm}" 並篩選標籤的結果`}
            {viewMode === 'all' && hasActiveSearchTerm && !hasActiveTag && `搜索 "${activeSearchTerm}" 的結果`}
            {viewMode === 'all' && !hasActiveSearchTerm && hasActiveTag && `標籤 "${availableTags.find(t => t.id === activeTagId)?.name}" 的文章`}
            {onlyRecommended && ' (僅推薦)'}
            ({totalCount} 個)
          </div>
        )}
      </div>
      )}
      
      {isReportedMode && (
        <div className="mb-6 space-y-4">
          <div className="text-sm text-gray-600">
            被檢舉貼文 ({totalCount} 個)
          </div>
          
          {/* 篩選和排序控制項 */}
          <div className="flex flex-wrap gap-3 items-center">
            {/* falseAlarm 篩選 */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">誤報篩選：</label>
              <select
                value={falseAlarmFilter}
                onChange={(e) => {
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    if (e.target.value === 'NONE') {
                      next.delete('falseAlarmFilter');
                    } else {
                      next.set('falseAlarmFilter', e.target.value);
                    }
                    next.set('page', '1');
                    return next;
                  });
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="NONE">全部</option>
                <option value="AT_LEAST_ONE">至少一個誤報</option>
                <option value="ALL">全部誤報</option>
                <option value="NO_FALSE_ALARM">完全沒有誤報</option>
              </select>
            </div>

            {/* 是否已檢舉篩選 */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">狀態：</label>
              <select
                value={isReportedFilter === null ? 'all' : isReportedFilter ? 'reported' : 'notReported'}
                onChange={(e) => {
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    if (e.target.value === 'all') {
                      next.delete('isReported');
                    } else {
                      next.set('isReported', e.target.value === 'reported' ? 'true' : 'false');
                    }
                    next.set('page', '1');
                    return next;
                  });
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="all">全部</option>
                <option value="reported">已檢舉</option>
                <option value="notReported">未檢舉</option>
              </select>
            </div>

            {/* 是否推薦篩選 */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">推薦：</label>
              <select
                value={isRecommendedFilter === null ? 'all' : isRecommendedFilter ? 'recommended' : 'notRecommended'}
                onChange={(e) => {
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    if (e.target.value === 'all') {
                      next.delete('isRecommended');
                    } else {
                      next.set('isRecommended', e.target.value === 'recommended' ? 'true' : 'false');
                    }
                    next.set('page', '1');
                    return next;
                  });
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="all">全部</option>
                <option value="recommended">推薦</option>
                <option value="notRecommended">非推薦</option>
              </select>
            </div>

            {/* 排序 */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">排序：</label>
              <select
                value={reportedSortBy}
                onChange={(e) => {
                  setSearchParams((prev) => {
                    const next = new URLSearchParams(prev);
                    if (e.target.value === 'REPORT_COUNT_DESC') {
                      next.delete('reportedSortBy');
                    } else {
                      next.set('reportedSortBy', e.target.value);
                    }
                    next.set('page', '1');
                    return next;
                  });
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="REPORT_COUNT_DESC">檢舉次數（高→低）</option>
                <option value="REPORT_COUNT_ASC">檢舉次數（低→高）</option>
                <option value="LATEST">最新</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* 投票攻略模式提示 */}
      {isStrategyMode && (
        <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border-2 border-purple-200">
          <div className="text-sm text-gray-700">
            <p className="font-semibold mb-2">📊 投票攻略</p>
            <p>基於您 like 過的使用者的擇友標準，為您推薦相關的投票文。優先顯示未作答的投票文。</p>
          </div>
        </div>
      )}

      {/* 投票列表 */}
      {articles.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-500 text-lg mb-4">
            {isStrategyMode 
              ? '還沒有 like 過任何使用者，或沒有找到相關的投票文' 
              : (searchTerm ? '沒有找到相關的投票' : '暫時沒有投票')}
          </div>
          {searchTerm && !isStrategyMode && (
            <button 
              onClick={() => setSearchTerm('')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              清除搜索
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-y-1 gap-x-3 md:gap-x-3 md:gap-y-2">
          {articles.map((article) => (
            <React.Fragment key={article.id}>
              {/* 桌面端顯示 */}
              <div className="hidden md:block">
                <VotingCard
                  article={article}
                  onVoteSuccess={handleVoteSuccess}
                  onViewDetail={handleViewDetail}
                  viewMode={viewMode || 'all'}
                  currentUserId={user?.id}
                />
              </div>
              {/* 移動端顯示 */}
              <div className="md:hidden">
                <VotingCardMobile
                  article={article}
                  onVoteSuccess={handleVoteSuccess}
                  onViewDetail={handleViewDetail}
                  viewMode={viewMode || 'all'}
                  currentUserId={user?.id}
                />
              </div>
            </React.Fragment>
          ))}
        </div>
      )}

      {/* 重新分析按鈕（投票攻略模式） */}
      {isStrategyMode && articles.length > 0 && (
        <div className="mt-8 flex justify-center">
          <button
            onClick={() => {
              refetchStrategy();
            }}
            className="px-6 py-3 bg-gradient-to-r from-pink-400 to-purple-500 text-white rounded-lg hover:from-pink-500 hover:to-purple-600 transition-all font-medium shadow-sm hover:shadow-md"
          >
            重新分析
          </button>
        </div>
      )}

      {/* 分頁組件 */}
      {!isStrategyMode && totalPages > 1 && (
        <div className="mt-8 flex justify-center items-center space-x-2">
          {/* 上一頁按鈕 */}
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-3 py-2 rounded-lg ${
              currentPage === 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            上一頁
          </button>

          {/* 頁碼按鈕 */}
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
            // 只顯示當前頁附近的頁碼
            if (
              page === 1 ||
              page === totalPages ||
              (page >= currentPage - 2 && page <= currentPage + 2)
            ) {
              return (
                <button
                  key={page}
                  onClick={() => handlePageChange(page)}
                  className={`px-3 py-2 rounded-lg ${
                    page === currentPage
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {page}
                </button>
              );
            } else if (
              page === currentPage - 3 ||
              page === currentPage + 3
            ) {
              return (
                <span key={page} className="px-2 text-gray-400">
                  ...
                </span>
              );
            }
            return null;
          })}

          {/* 下一頁按鈕 */}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-3 py-2 rounded-lg ${
              currentPage === totalPages
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            下一頁
          </button>
        </div>
      )}

      {/* 分頁信息 */}
      {!isStrategyMode && totalCount > 0 && (
        <div className="mt-4 text-center text-sm text-gray-600">
          顯示第 {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, totalCount)} 條，
          共 {totalCount} 條投票，第 {currentPage} / {totalPages} 頁
        </div>
      )}

      {/* 投票攻略模式統計信息 */}
      {isStrategyMode && totalCount > 0 && (
        <div className="mt-4 text-center text-sm text-gray-600">
          共 {totalCount} 條推薦投票文
        </div>
      )}

      {/* 投票詳情彈窗 */}
      <VotingDetailModal
        isOpen={selectedVotingId !== null}
        onClose={handleCloseDetail}
        votingId={selectedVotingId}
        viewMode={viewMode || 'all'}
        currentUserId={user?.id}
      />
    </div>
  );
};

export default VotingList; 